import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  useToast,
  Switch,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import actions from "../actions";
import {
  KYDOrganizationFull,
  KYDOrganizationSettings,
} from "@common/interfaces/KYDOrganization";
import { useDialog } from "../Common/Dialog";

const Settings = () => {
  const [organization, setOrganization] = useState<KYDOrganizationFull | null>(
    null
  );

  const [loading, setLoading] = useState<any>({});

  const toast = useToast();
  const dialog = useDialog();

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const fetchSettings = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        updateLoading("init", true);
      }
      let org;
      try {
        const _ = await actions.ADMIN.fetchOrganization();
        if (_) {
          setOrganization(_);
          org = _;
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return org;
    },
    [updateLoading, dialog]
  );

  useEffect(() => {
    fetchSettings();
  }, [updateLoading, fetchSettings]);

  const onUpdateSettings = useCallback(
    async (settings: Partial<KYDOrganizationSettings>) => {
      updateLoading("organization", true);
      try {
        await actions.ADMIN.updateOrganization("settings", settings);
        await fetchSettings(true);
        toast({
          title: "Settings Updated",
          description: `Your settings have been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("organization", false);
    },
    [fetchSettings, updateLoading, dialog]
  );

  return (
    <VStack w="100%">
      <Stack w="100%" maxW={"500px"} spacing={{ base: "8", lg: "6" }}>
        <Stack
          spacing="4"
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "center" }}
        >
          <Stack spacing="1">
            <Heading
              size={useBreakpointValue({ base: "xs", lg: "sm" })}
              fontWeight="medium"
            >
              <HStack>
                <Text fontSize={"lg"} fontWeight="bold">
                  Settings
                </Text>
              </HStack>
            </Heading>
          </Stack>
        </Stack>

        {loading["init"] && (
          <VStack>
            <Spinner />
            <Text>Loading settings...</Text>
          </VStack>
        )}

        {organization && (
          <Stack spacing={{ base: "5", lg: "6" }}>
            <Stack>
              <Text fontWeight={"medium"}>Reminders</Text>
              <HStack
                justify={"space-between"}
                p={2}
                borderWidth={"1px"}
                rounded={"md"}
                borderColor={"gray.200"}
              >
                <Text>24 Hour Reminder Texts</Text>
                <Switch
                  defaultChecked={
                    !organization.settings.supress_first_reminder_sms
                  }
                  isDisabled={loading.organization}
                  colorScheme={"green"}
                  onChange={(e) =>
                    onUpdateSettings({
                      supress_first_reminder_sms: !e.target.checked,
                      supress_second_reminder_sms:
                        !!organization.settings.supress_second_reminder_sms,
                    })
                  }
                />
              </HStack>
              <HStack
                justify={"space-between"}
                p={2}
                borderWidth={"1px"}
                rounded={"md"}
                borderColor={"gray.200"}
              >
                <Text>1 Hour Reminder Texts</Text>
                <Switch
                  defaultChecked={
                    !organization.settings.supress_second_reminder_sms
                  }
                  isDisabled={loading.organization}
                  colorScheme={"green"}
                  onChange={(e) =>
                    onUpdateSettings({
                      supress_first_reminder_sms:
                        !!organization.settings.supress_first_reminder_sms,
                      supress_second_reminder_sms: !e.target.checked,
                    })
                  }
                />
              </HStack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </VStack>
  );
};

export default Settings;
